import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, isAxiosError } from 'axios';
import { camelizeKeys, decamelizeKeys } from 'humps';

import { BASE_API_URL } from '~/constants/common';
import { HTTP_STATUSE_CODES } from '~/constants/http-statuses-constants';
import {
  camelizeProcessor,
  decamelizeProcessor,
  logOut,
  parseErrorResponseMessage,
  refreshToken,
} from '~/utils/common';
import { logger } from '~/utils/logger';

import { cookieService } from './cookie-service';
import { googleAnalytics } from './google-analytics-service';

const token = cookieService.getCookie('access');

axios.defaults.baseURL = BASE_API_URL;
axios.defaults.headers.common['Content-Type'] = 'application/json';
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

axios.interceptors.request.use(
  function (config: AxiosRequestConfig): AxiosRequestConfig {
    if (!config.headers) config.headers = {};
    config.headers['Accept-Language'] = localStorage.getItem('language') || 'en';
    return config;
  },
  function (error: AxiosError): Promise<AxiosError> {
    return Promise.reject(error);
  },
);

axios.interceptors.request.use(
  function (config: AxiosRequestConfig): AxiosRequestConfig {
    if (!config.headers) config.headers = {};
    const gaClientId = googleAnalytics.methods.getGa4ClientId();

    config.headers[googleAnalytics.constants.GA_CLIENT_ID] = gaClientId;
    return config;
  },
  function (error: AxiosError): Promise<AxiosError> {
    return Promise.reject(error);
  },
);

axios.interceptors.request.use((request: AxiosRequestConfig): AxiosRequestConfig => {
  const isJson =
    request.headers?.['Content-Type'] === 'application/json' ||
    request.headers?.['content-type'] === 'application/json';
  if (request.data && isJson) request.data = decamelizeKeys(request.data, decamelizeProcessor);
  if (request.params) request.params = decamelizeKeys(request.params, decamelizeProcessor);
  return request;
});

axios.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    const isJson =
      response.headers?.['Content-Type'] === 'application/json' ||
      response.headers?.['content-type'] === 'application/json';
    if (response.data && isJson) response.data = camelizeKeys(response.data, camelizeProcessor);
    return response;
  },
  (error: AxiosError): Promise<never> => {
    error.message = parseErrorResponseMessage(error, error.message);
    throw error;
  },
);

axios.interceptors.request.use(
  async (config: AxiosRequestConfig): Promise<AxiosRequestConfig> => {
    const token = cookieService.getCookie('access');
    if (!config.headers) config.headers = {};
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError): Promise<AxiosError> => {
    return Promise.reject(error);
  },
);

axios.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    return response;
  },
  async (error: AxiosError): Promise<never> => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    const codeStatus = error?.response?.status;
    const url = originalRequest.url ?? '';
    const refreshFailed = isRefreshTokenRequest(url) && error.response?.status === 401;

    if (refreshFailed || codeStatus === 403) {
      await logOut();
    }

    if (!isAxiosError(error)) {
      throw error;
    }

    if (codeStatus === HTTP_STATUSE_CODES.UNAUTHORIZED) {
      const _refreshToken = cookieService.getCookie('refresh');
      if (_refreshToken) {
        await refreshToken();
        return axios(originalRequest);
      } else {
        return Promise.reject(error);
      }
    } else {
      return Promise.reject(error);
    }
  },
);

export const apiClient = {
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return await axios.get<T>(url, {
        ...config,
        paramsSerializer: {
          indexes: null, // no brackets at all
        },
      });
    } catch (error) {
      await handleResponseError(error);
      throw error;
    }
  },
  async post<T = any>(url: string, data?: any, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
    try {
      return await axios.post<T>(url, data, config);
    } catch (error) {
      await handleResponseError(error);
      throw error;
    }
  },
  async put<T = any>(url: string, data?: any): Promise<AxiosResponse<T>> {
    try {
      return await axios.put<T>(url, data);
    } catch (error) {
      await handleResponseError(error);
      throw error;
    }
  },
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    try {
      return await axios.delete<T>(url, config);
    } catch (error) {
      await handleResponseError(error);
      throw error;
    }
  },
  async patch<T = any>(url: string, data?: any): Promise<AxiosResponse<T>> {
    try {
      return await axios.patch<T>(url, data);
    } catch (error) {
      await handleResponseError(error);
      throw error;
    }
  },
};

const handleResponseError = async (data: unknown): Promise<void> => {
  logger.error(data, data?.config, 11111);
};

function isRefreshTokenRequest(url: string): boolean {
  return url.includes('/account/token/refresh');
}
